import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'ghost';
type Size = 'md' | 'sm';

interface ButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: Variant;
  size?: Size;
  /** Trailing icon, e.g. an arrow for a link that moves down or off the page. */
  icon?: ReactNode;
}

const base =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-[background-color,border-color,color,box-shadow,transform] duration-300 ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const variants: Record<Variant, string> = {
  primary:
    'bg-primary text-primary-foreground shadow-[0_12px_34px_hsl(var(--primary)/0.28)] hover:-translate-y-px hover:bg-primary/90',
  ghost:
    'border border-foreground/25 text-foreground hover:border-brand-sky/50 hover:bg-foreground/5',
};

const sizes: Record<Size, string> = {
  md: 'min-h-12 px-6 text-[0.92rem]',
  sm: 'min-h-10 px-4 text-[0.82rem]',
};

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  className,
  children,
  target,
  rel,
  ...rest
}: ButtonProps) {
  const safeRel = target === '_blank' ? (rel ?? 'noreferrer') : rel;
  return (
    <a
      className={cn(base, variants[variant], sizes[size], className)}
      target={target}
      rel={safeRel}
      {...rest}
    >
      {children}
      {icon}
    </a>
  );
}
